// src/views/admin/MailPage.js
// Enhanced Production-Ready Mail Center Page
// Features: FlowSense Context Linking, FormData Attachments, Infinite Loop Fix, Modern UI/UX
//
// --- UPDATE (SLA AI) -------------------------------------------------------------
//   • New "AI Escalations" tab: the SLA escalation emails FlowSense AI sent to
//     supervisors / managers (recipients, failures, the AI's reasoning) with
//     "Draft follow-up", which fills Compose (subject, body, work order link) and
//     switches to the Compose tab. Tabs are now controlled so this can happen.
//   • Sent History marks emails sent by FlowSense AI.
//   • FIX: the fake upload-progress interval kept running (and setting state) after a
//     fast send or an unmount — it is now cleared in finally / on unmount.
//   • FIX: draft cards had two `transition` props (framer + CSS); the CSS one silently
//     replaced the stagger animation.
//   • FIX: `isTruncated` on Td / Text leaked to the DOM (React unknown-prop warning in
//     Chakra v2) — replaced with noOfLines / ellipsis style props.
// --- END UPDATE ------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Heading, VStack, FormControl, FormLabel, Input, Button,
  useToast, useColorModeValue, Flex, Icon, Tabs, TabList, TabPanels,
  Tab, TabPanel, Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton,
  HStack, Divider, Text, Alert, AlertIcon, Select, AlertTitle, AlertDescription,
  Badge, Tooltip, useDisclosure, Menu, MenuButton, MenuList, MenuItem,
  InputGroup, InputRightElement, InputLeftElement, Card, CardBody,
  Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid, Spinner,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
  DrawerCloseButton, DrawerFooter, Skeleton, Tag, TagLabel, Avatar
} from '@chakra-ui/react';
import { 
  MdSend, MdRefresh, MdDelete, MdSave, MdNavigateBefore, MdNavigateNext,
  MdAttachFile, MdSchedule, MdDrafts, MdMoreVert, MdClose, MdEmail,
  MdPeople, MdHistory, MdSearch, MdBugReport, MdDownload, MdContacts,
  MdInbox, MdCheckCircle, MdError, MdOutbox, MdLink, MdBuild, MdWarning
} from 'react-icons/md';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import TemplateManagement from '../../components/mail/TemplateManagement';
// --- UPDATE: SLA AI escalation emails
import SlaEscalationMailPanel from '../../components/sla/ai/SlaEscalationMailPanel';
import { MdAutoAwesome } from 'react-icons/md';
// --- END UPDATE

// Framer Motion for animations
import { motion, AnimatePresence } from 'framer-motion';

// Import config & auth utils
import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';
import { format } from 'date-fns';

// Import utilities
import { 
  useDebounce, 
  useAsync, 
  usePagination, 
  useAutoSave,
  useLocalStorage,
  useRateLimiter 
} from '../../utils/hooks';
import ErrorBoundary from '../../components/ErrorBoundary';
import { 
  LoadingOverlay, 
  ConfirmDialog, 
  EmptyState,
  DataStatus,
  SearchInput,
  LoadingProgress
} from '../../components/common/LoadingComponents';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// ============================================================================
// DEBUG CONFIGURATION & LOGGER
// ============================================================================
const DEBUG_CONFIG = {
    enabled: process.env.NODE_ENV === 'development',
    logLevel: 'all',
    logToConsole: true,
};

class DebugLogger {
    constructor(componentName) {
        this.componentName = componentName;
        this.logs = [];
        this.maxLogs = 500;
        this.enabled = DEBUG_CONFIG.enabled;
        this.listeners = [];
    }

    log(level, message, data = null) {
        if (!this.enabled) return;

        const timestamp = new Date().toISOString();
        let logData = data;

        if (data instanceof Error) {
            logData = {
                message: data.message,
                stack: data.stack,
                name: data.name,
            };
        }

        const logEntry = {
            timestamp,
            level,
            message,
            data: logData,
            component: this.componentName,
        };

        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }

        this.listeners.forEach(listener => listener(logEntry));

        if (this.enabled && DEBUG_CONFIG.logToConsole) {
            const prefix = `[${this.componentName}][${level.toUpperCase()}]`;
            const style = this.getConsoleStyle(level);

            if (logData) {
                console.log(`%c${prefix} ${message}`, style, logData);
            } else {
                console.log(`%c${prefix} ${message}`, style);
            }
        }
    }

    getConsoleStyle(level) {
        const styles = {
            debug: 'color: #888; font-size: 11px',
            info: 'color: #3182ce; font-weight: bold',
            warn: 'color: #d69e2e; font-weight: bold',
            error: 'color: #e53e3e; font-weight: bold',
            success: 'color: #38a169; font-weight: bold',
        };
        return styles[level] || styles.info;
    }

    debug(message, data) { this.log('debug', message, data); }
    info(message, data) { this.log('info', message, data); }
    warn(message, data) { this.log('warn', message, data); }
    error(message, data) { this.log('error', message, data); }
    success(message, data) { this.log('success', message, data); }

    subscribe(callback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    getLogs(level = null) {
        if (level) {
            return this.logs.filter(log => log.level === level);
        }
        return [...this.logs];
    }

    clear() {
        this.logs = [];
    }

    export() {
        return JSON.stringify(this.logs, null, 2);
    }
}

// ============================================================================
// API CALLS
// ============================================================================
const API_BASE_URL = config.apiUrl;

const handleApiError = async (response, resourceName = 'resource') => {
  let errorMsg = `HTTP error ${response.status} fetching ${resourceName}: ${response.statusText}`;
  if (response.headers.get("content-type")?.includes("application/json")) {
    try {
      const errData = await response.json();
      errorMsg = errData.message || (errData.errors ? errData.errors.map(e => e.msg).join(', ') : errorMsg);
    } catch (parseErr) { /* Ignore parsing error */ }
  }
  throw new Error(errorMsg);
};

// ENHANCEMENT: Switched to handle both FormData and JSON seamlessly
const sendEmailApi = async (payload, isFormData, token) => {
  console.log("API: Sending Email");
  const headers = { 'Authorization': `Bearer ${token}` };
  if (!isFormData) {
      headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE_URL}/mail/send`, {
    method: 'POST', 
    headers: headers, 
    body: isFormData ? payload : JSON.stringify(payload),
  });
  if (!response.ok) await handleApiError(response, 'sending email');
  return response.json();
};

const fetchEmailTemplates = async (token) => {
    console.log("API: Fetching email templates");
    const response = await fetch(`${API_BASE_URL}/email-templates`, { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    if (!response.ok) await handleApiError(response, 'email templates');
    return response.json();
};

const fetchSentEmails = async (token, page = 1, limit = 15) => {
  console.log(`API: Fetching sent emails (page: ${page}, limit: ${limit})`);
  const response = await fetch(`${API_BASE_URL}/sent-emails?page=${page}&limit=${limit}`, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  if (!response.ok) await handleApiError(response, 'sent emails');
  return await response.json();
};

const fetchEmailContacts = async (token) => {
  console.log("API: Fetching contacts");
  const response = await fetch(`${API_BASE_URL}/contacts`, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  if (!response.ok) await handleApiError(response, 'contacts');
  return await response.json();
};

const createEmailContact = async (contactData, token) => {
  console.log("API: Creating contact:", contactData);
  const response = await fetch(`${API_BASE_URL}/contacts`, { 
    method: 'POST', 
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${token}` 
    }, 
    body: JSON.stringify(contactData) 
  });
  if (!response.ok) await handleApiError(response, 'creating contact');
  return await response.json();
};

const deleteContact = async (id, token) => {
  console.log(`API: Deleting contact ID: ${id}`);
  const response = await fetch(`${API_BASE_URL}/contacts/${id}`, { 
    method: 'DELETE', 
    headers: { 'Authorization': `Bearer ${token}` } 
  });
  if (!response.ok) await handleApiError(response, 'deleting contact');
  try { 
    return await response.json(); 
  } catch (e) { 
    if (response.status === 200 || response.status === 204) 
      return { message: 'Contact deleted successfully' }; 
    throw new Error("Error parsing delete response."); 
  }
};

// --- Helper Functions ---
const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : 'N/A');

// ============================================================================
// ENHANCED STAT CARD COMPONENT
// ============================================================================
const EnhancedStatCard = ({ label, value, icon: IconComponent, color = 'blue', helpText, isLoading }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const labelColor = useColorModeValue('gray.600', 'gray.400');
    const valueColor = useColorModeValue('gray.800', 'white');
    const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
    const iconColor = useColorModeValue(`${color}.500`, `${color}.300`);
    const gradientBg = useColorModeValue(
        `linear(to-br, ${color}.50, white)`,
        `linear(to-br, ${color}.900, gray.800)`
    );

    return (
        <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, boxShadow: 'lg' }}
            transition={{ duration: 0.2 }}
        >
            <Box
                bg={cardBg}
                bgGradient={gradientBg}
                p={5}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow="md"
                position="relative"
                overflow="hidden"
                _hover={{ borderColor: `${color}.400` }}
                transition="all 0.3s"
            >
                <Box
                    position="absolute" top="-20px" right="-20px" width="80px" height="80px"
                    bg={iconBg} opacity={0.3} borderRadius="full" filter="blur(15px)"
                />

                <Stat position="relative" zIndex={1}>
                    <Flex justify="space-between" align="flex-start">
                        <Box flex={1}>
                            <StatLabel color={labelColor} fontSize="sm" fontWeight="600" mb={1}>{label}</StatLabel>
                            {isLoading ? (
                                <Skeleton height="36px" width="60px" />
                            ) : (
                                <StatNumber fontSize="3xl" fontWeight="bold" color={valueColor} lineHeight="1">{value}</StatNumber>
                            )}
                            {helpText && <StatHelpText fontSize="xs" color={labelColor} mt={1} mb={0}>{helpText}</StatHelpText>}
                        </Box>
                        <Box bg={iconBg} p={3} borderRadius="xl" boxShadow="sm">
                            <Icon as={IconComponent} boxSize={6} color={iconColor} />
                        </Box>
                    </Flex>
                </Stat>
            </Box>
        </MotionBox>
    );
};

// ============================================================================
// ENHANCED TAB COMPONENT
// ============================================================================
const EnhancedTab = ({ children, icon, count, isSelected }) => {
    const grayHover = useColorModeValue('gray.100', 'gray.700');

    return (
        <Tab
            borderRadius="xl"
            fontWeight="600"
            _selected={{ bg: 'blue.500', color: 'white', boxShadow: 'md' }}
            _hover={{ bg: isSelected ? 'blue.600' : grayHover }}
            transition="all 0.2s"
            px={4}
            py={3}
        >
            <HStack spacing={2}>
                <Icon as={icon} boxSize={4} />
                <Text>{children}</Text>
                {count !== undefined && (
                    <Badge colorScheme={isSelected ? 'whiteAlpha' : 'blue'} borderRadius="full" px={2} fontSize="xs">
                        {count}
                    </Badge>
                )}
            </HStack>
        </Tab>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function MailPageEnhanced() {
  const logger = useRef(new DebugLogger('MailPage')).current;

  // Compose State
  const [sendMode, setSendMode] = useState('single');
  const [to, setTo] = useState('');
  const [toRole, setToRole] = useState('tech');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugFilter, setDebugFilter] = useState('all');

  // ENHANCEMENT: Context Linking State
  const [linkType, setLinkType] = useState('none');
  const [linkId, setLinkId] = useState('');

  // Draft management
  const [draftName, setDraftName] = useState('');
  const [savedDrafts, setSavedDrafts] = useLocalStorage('email-drafts', []);
  
  // ENHANCEMENT: FIX INFINITE AUTOSAVE LOOP
  // Memoize the dependency payload so useAutoSave doesn't fire endlessly on re-renders
  const draftData = useMemo(() => ({
    to, subject, body, sendMode, toRole, linkType, linkId
  }), [to, subject, body, sendMode, toRole, linkType, linkId]);
  
  const { lastSaved, isSaving } = useAutoSave('current-email-draft', draftData, 2000);
  
  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Template State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // History/Contact State
  const [sentEmails, setSentEmails] = useState([]);
  const [emailContacts, setEmailContacts] = useState([]);
  
  // Search and Rate Limiting
  const [historySearch, setHistorySearch] = useState('');
  const debouncedHistorySearch = useDebounce(historySearch, 300);
  const [contactSearch, setContactSearch] = useState('');
  const debouncedContactSearch = useDebounce(contactSearch, 300);
  const checkRateLimit = useRateLimiter(10, 60000); 
  
  // Loading States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(null);

  const pagination = usePagination(sentEmails.length, 15);
  
  // Delete confirmation
  const [contactToDelete, setContactToDelete] = useState(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isDebugOpen, onOpen: onDebugOpen, onClose: onDebugClose } = useDisclosure();
  
  const toast = useToast();

  // --- UPDATE: controlled tabs (AI Escalations → Compose) + progress timer ref ---
  const [tabIndex, setTabIndex] = useState(0);
  const progressTimerRef = useRef(null);
  useEffect(() => () => clearInterval(progressTimerRef.current), []);
  // --- END UPDATE ---

  // ============================================================================
  // THEME COLORS (All hooks called unconditionally at top level)
  // ============================================================================
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue("white", "gray.800");
  const formLabelColor = useColorModeValue("gray.700", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.500", "gray.400");
  const theadBg = useColorModeValue("gray.50", "gray.700");
  const trHoverBg = useColorModeValue("blue.50", "blue.900");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const accentColor = useColorModeValue("blue.500", "blue.400");
  const successColor = useColorModeValue("green.500", "green.400");
  const dangerColor = useColorModeValue("red.500", "red.400");
  const inputBg = useColorModeValue("white", "gray.700");
  const debugRowBg = useColorModeValue('gray.100', 'gray.700');
  const debugVStackBg = useColorModeValue('gray.50', 'gray.900');
  const attachmentBg = useColorModeValue('gray.100', 'gray.700');
  const linkBoxBg = useColorModeValue('blue.50', 'blue.900');
  const linkBoxBorder = useColorModeValue('blue.200', 'blue.700');
  const headerGradient = useColorModeValue(
    'linear(to-r, blue.500, cyan.500)',
    'linear(to-r, blue.400, cyan.400)'
  );
  const tabGradient = useColorModeValue(
    'linear(to-br, white, blue.50)',
    'linear(to-br, gray.800, blue.900)'
  );

  // ============================================================================
  // DEBUG LOG SUBSCRIPTION
  // ============================================================================
  useEffect(() => {
      const unsubscribe = logger.subscribe((logEntry) => {
          setDebugLogs(prev => [...prev.slice(-99), logEntry]);
      });
      return unsubscribe;
  }, [logger]);

  const filteredDebugLogs = useMemo(() => {
      if (debugFilter === 'all') return debugLogs;
      return debugLogs.filter(log => log.level === debugFilter);
  }, [debugLogs, debugFilter]);

  // ============================================================================
  // ASYNC DATA LOADING
  // ============================================================================
  const fetchHistoryCallback = useCallback(async (page = 1) => {
    const token = getAuthToken();
    
    // FIX: Fail gracefully instead of throwing an error if token isn't ready
    if (!token) {
      logger.warn('Authentication token not ready, skipping history fetch.');
      return { data: [], pagination: { totalItems: 0, totalPages: 1, currentPage: 1, itemsPerPage: 15 } };
    }
    
    const result = await fetchSentEmails(token, page, 15);
    
    if (result && Array.isArray(result.data) && result.pagination) {
      setSentEmails(result.data);
      logger.success('Email history loaded', { count: result.data.length });
      return result;
    } else {
      throw new Error("Received invalid data format from server.");
    }
  }, [logger]);

  const fetchContactsCallback = useCallback(async () => {
    const token = getAuthToken();
    
    // FIX: Fail gracefully instead of throwing an error
    if (!token) {
      logger.warn('Authentication token not ready, skipping contacts fetch.');
      return [];
    }
    
    const data = await fetchEmailContacts(token);
    setEmailContacts(Array.isArray(data) ? data : []);
    logger.success('Contacts loaded', { count: data?.length || 0 });
    return data;
  }, [logger]);

  const { loading: isLoadingHistory, error: historyError, execute: loadSentEmails } = useAsync(fetchHistoryCallback);
  const { loading: isLoadingContacts, error: contactsError, execute: loadContacts } = useAsync(fetchContactsCallback);

  const loadEmailTemplates = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    
    try {
        const templates = await fetchEmailTemplates(token);
        setEmailTemplates(templates);
        logger.success('Templates loaded', { count: templates?.length || 0 });
    } catch (error) {
        logger.error('Error loading templates', error);
        toast({ title: "Error loading templates", description: error.message, status: "error" });
    }
  }, [toast, logger]);

  useEffect(() => {
    logger.info('Component mounted');
    loadSentEmails(1);
    loadContacts();
    loadEmailTemplates();
  }, [loadSentEmails, loadContacts, loadEmailTemplates, logger]);

  // ============================================================================
  // FILTERED DATA
  // ============================================================================
  const filteredHistory = useMemo(() => {
    if (!debouncedHistorySearch) return sentEmails;
    const search = debouncedHistorySearch.toLowerCase();
    return sentEmails.filter(email => 
      email.recipient_email?.toLowerCase().includes(search) ||
      email.subject?.toLowerCase().includes(search) ||
      email.body_preview?.toLowerCase().includes(search)
    );
  }, [sentEmails, debouncedHistorySearch]);

  const filteredContacts = useMemo(() => {
    if (!debouncedContactSearch) return emailContacts;
    const search = debouncedContactSearch.toLowerCase();
    return emailContacts.filter(contact => 
      contact.email?.toLowerCase().includes(search) ||
      contact.name?.toLowerCase().includes(search)
    );
  }, [emailContacts, debouncedContactSearch]);

  const currentHistoryData = useMemo(() => {
    return filteredHistory.slice(
      pagination.offset,
      pagination.offset + pagination.itemsPerPage
    );
  }, [filteredHistory, pagination.offset, pagination.itemsPerPage]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleTemplateChange = useCallback((templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) {
        setSubject('');
        setBody('');
        return;
    }
    const template = emailTemplates.find(t => t.id === parseInt(templateId));
    if (template) {
        setSubject(template.subject);
        setBody(template.body);
        logger.info('Template applied', { template: template.name });
    }
  }, [emailTemplates, logger]);

  const handleSaveDraft = useCallback(() => {
    const draft = {
      id: Date.now(),
      name: draftName || `Draft ${new Date().toLocaleString()}`,
      to, subject, body, sendMode, toRole, linkType, linkId,
      savedAt: new Date().toISOString(),
    };
    
    setSavedDrafts(prev => [...prev, draft]);
    setDraftName('');
    logger.success('Draft saved', { name: draft.name });
    
    toast({
      title: "Draft Saved",
      description: `Saved as "${draft.name}"`,
      status: "success",
      duration: 2000,
    });
  }, [to, subject, body, sendMode, toRole, linkType, linkId, draftName, setSavedDrafts, toast, logger]);

  const handleLoadDraft = useCallback((draft) => {
    setTo(draft.to || '');
    setSubject(draft.subject || '');
    setBody(draft.body || '');
    setSendMode(draft.sendMode || 'single');
    setToRole(draft.toRole || 'tech');
    setLinkType(draft.linkType || 'none');
    setLinkId(draft.linkId || '');
    logger.info('Draft loaded', { name: draft.name });
    
    toast({
      title: "Draft Loaded",
      description: `Loaded "${draft.name}"`,
      status: "info",
      duration: 2000,
    });
  }, [toast, logger]);

  const handleDeleteDraft = useCallback((draftId) => {
    setSavedDrafts(prev => prev.filter(d => d.id !== draftId));
    logger.info('Draft deleted');
    toast({ title: "Draft Deleted", status: "success", duration: 2000 });
  }, [setSavedDrafts, toast, logger]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit`,
          status: "warning",
        });
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    logger.info('Files attached', { count: validFiles.length });
  }, [toast, logger]);

  // --- UPDATE: "Draft follow-up" from an SLA AI escalation email ---
  const handleAiDraft = useCallback((draft) => {
    setSendMode('single');
    setSelectedTemplate('');
    setSubject(draft.subject || '');
    setBody(draft.body || '');
    setLinkType(draft.linkType || 'none');
    setLinkId(draft.linkId || '');
    setTabIndex(0);
    logger.info('AI follow-up drafted', { linkId: draft.linkId });
    toast({ title: 'Follow-up drafted', description: 'Add the recipient and send.', status: 'info', duration: 3000 });
  }, [toast, logger]);
  // --- END UPDATE ---

  const handleRemoveAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleComposeSubmit = async (e) => {
    e.preventDefault();
    
    const isSingleSend = sendMode === 'single';

    if ((isSingleSend && !to) || !subject || !body) {
        toast({ title: "Missing Fields", description: "Please fill in all required fields.", status: "warning" });
        return;
    }
    
    try {
      checkRateLimit();
    } catch (error) {
      toast({ title: "Rate Limit Exceeded", description: error.message, status: "warning", duration: 5000 });
      return;
    }
    
    setIsSubmitting(true);
    logger.info('Sending email', { sendMode, to: isSingleSend ? to : toRole });
    
    const token = getAuthToken();
    if (!token) {
        toast({ title: "Auth Error", status: "error" });
        setIsSubmitting(false);
        return;
    }

    // ENHANCEMENT: FormData vs JSON Handling for Files + Context Linking
    const hasAttachments = attachments.length > 0;
    let payload;

    if (hasAttachments) {
      payload = new FormData();
      payload.append('subject', subject.trim());
      payload.append('html', body);
      payload.append('text', body.replace(/<[^>]+>/g, ''));
      if (isSingleSend) payload.append('to', to.trim());
      else payload.append('user_role', toRole);
      
      if (linkType !== 'none' && linkId) {
        payload.append('contextType', linkType);
        payload.append('contextId', linkId);
      }
      if (selectedTemplate) payload.append('template', selectedTemplate);

      attachments.forEach(file => payload.append('attachments', file));
    } else {
      payload = {
          subject: subject.trim(),
          html: body,
          text: body.replace(/<[^>]+>/g, ''),
          ...(isSingleSend ? { to: to.trim() } : { user_role: toRole }),
          ...(linkType !== 'none' && linkId && { contextType: linkType, contextId: linkId }),
          ...(selectedTemplate && { template: selectedTemplate, data: {} })
      };
    }

    if (hasAttachments) {
      setUploadProgress(0);
      // --- UPDATE: interval tracked in a ref and always cleared ---
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) { clearInterval(progressTimerRef.current); return 90; }
          return prev + 10;
        });
      }, 100);
    }

    try {
      const result = await sendEmailApi(payload, hasAttachments, token);
      
      logger.success('Email sent successfully', result);
      toast({ title: "Emails Queued", description: result.message, status: "success", duration: 5000 });
      
      setTo(''); 
      setSubject(''); 
      setBody(''); 
      setSelectedTemplate('');
      setAttachments([]);
      setLinkId('');
      setLinkType('none');
      setUploadProgress(0);
      
      loadSentEmails(1);
    } catch (error) {
      logger.error('Error sending email', error);
      toast({ title: "Error Sending Email", description: error.message, status: "error", duration: 7000 });
    } finally {
      clearInterval(progressTimerRef.current); // --- UPDATE ---
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleSaveContact = async () => {
    if (!to || !to.includes('@')) {
      toast({ title: "Invalid Email", description: "Please enter a valid email address.", status: "warning" });
      return;
    }
    
    const token = getAuthToken();
    if (!token) {
      toast({ title: "Auth Error", status: "error"});
      return;
    }
    
    if (emailContacts.some(c => c.email === to.trim())) {
      toast({ title: "Contact Exists", status: "info", description: "This email is already in your contacts." });
      return;
    }
    
    setIsSavingContact(true);
    try {
      await createEmailContact({ email: to.trim() }, token);
      logger.success('Contact saved', { email: to.trim() });
      toast({ title: "Contact Saved", status: "success" });
      loadContacts();
    } catch (error) {
      logger.error('Error saving contact', error);
      toast({ title: "Error Saving Contact", description: error.message, status: "error" });
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    setContactToDelete(contactId);
    onDeleteOpen();
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;
    
    const token = getAuthToken();
    if (!token) {
      toast({ title: "Auth Error", status: "error"});
      onDeleteClose();
      return;
    }
    
    setIsDeletingContact(contactToDelete);
    
    try {
      await deleteContact(contactToDelete, token);
      logger.success('Contact deleted');
      toast({ title: "Contact Deleted", status: "success" });
      loadContacts();
    } catch (error) {
      logger.error('Error deleting contact', error);
      toast({ title: "Error Deleting Contact", description: error.message, status: "error" });
    } finally {
      setIsDeletingContact(null);
      setContactToDelete(null);
      onDeleteClose();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <ErrorBoundary>
      <Box pt={{ base: "10px", md: "10px", xl: "10px" }} bg={bgColor} minH="100vh" px={{ base: 4, md: 6 }}>
        {/* ====================== HEADER ====================== */}
        <MotionFlex
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          justify="space-between"
          align="center"
          mb={6}
          wrap="wrap"
          gap={4}
        >
          <HStack spacing={4}>
            <MotionBox
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, type: 'spring' }}
              bgGradient={headerGradient}
              p={3}
              borderRadius="xl"
              boxShadow="lg"
            >
              <Icon as={MdEmail} color="white" boxSize={7} />
            </MotionBox>
            <Box>
              <Heading as="h2" size="xl" fontWeight="800" bgGradient={headerGradient} bgClip="text">
                Mail Center
              </Heading>
              <HStack spacing={2} mt={1}>
                {isSaving && (
                  <Tag colorScheme="blue" size="sm" borderRadius="full">
                    <Spinner size="xs" mr={2} />
                    <TagLabel>Auto-saving...</TagLabel>
                  </Tag>
                )}
                {lastSaved && !isSaving && (
                  <Text fontSize="xs" color={mutedColor}>
                    Last saved: {format(new Date(lastSaved), 'HH:mm:ss')}
                  </Text>
                )}
              </HStack>
            </Box>
          </HStack>

          <HStack spacing={2}>
            {DEBUG_CONFIG.enabled && (
              <Tooltip label="Debug Panel" hasArrow>
                <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                  <IconButton icon={<MdBugReport />} onClick={onDebugOpen} variant="ghost" colorScheme="yellow" borderRadius="full" size="md" />
                </MotionBox>
              </Tooltip>
            )}
            <Tooltip label="Refresh All" hasArrow>
              <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <IconButton icon={<MdRefresh />} onClick={() => { loadSentEmails(1); loadContacts(); loadEmailTemplates(); }} variant="ghost" colorScheme="blue" borderRadius="full" size="md" />
              </MotionBox>
            </Tooltip>
          </HStack>
        </MotionFlex>

        {/* ====================== STATS CARDS ====================== */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <EnhancedStatCard label="Emails Sent" value={sentEmails.length} icon={MdOutbox} color="blue" helpText="Total sent" />
          <EnhancedStatCard label="Contacts" value={emailContacts.length} icon={MdContacts} color="green" helpText="Saved contacts" />
          <EnhancedStatCard label="Drafts" value={savedDrafts.length} icon={MdDrafts} color="orange" helpText="Saved drafts" />
          <EnhancedStatCard label="Templates" value={emailTemplates.length} icon={MdInbox} color="purple" helpText="Available templates" />
        </SimpleGrid>

        {/* ====================== TABS ====================== */}
        <Tabs variant="soft-rounded" colorScheme="blue" isLazy index={tabIndex} onChange={setTabIndex}>
          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <TabList mb={4} bg={cardBg} p={3} borderRadius="2xl" boxShadow="md" borderWidth="1px" borderColor={borderColor} overflowX="auto" css={{ '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: borderColor, borderRadius: '3px' } }}>
              <EnhancedTab icon={MdSend}>Compose Email</EnhancedTab>
              <EnhancedTab icon={MdHistory} count={sentEmails.length}>Sent History</EnhancedTab>
              <EnhancedTab icon={MdContacts} count={emailContacts.length}>Contacts</EnhancedTab>
              <EnhancedTab icon={MdDrafts} count={savedDrafts.length}>Drafts</EnhancedTab>
              <EnhancedTab icon={MdInbox}>Templates</EnhancedTab>
              <EnhancedTab icon={MdAutoAwesome}>AI Escalations</EnhancedTab>{/* --- UPDATE --- */}
            </TabList>
          </MotionBox>
          
          <TabPanels>
            {/* ====================== COMPOSE TAB ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                <Box bg={cardBg} bgGradient={tabGradient} p={{ base: '4', md: '6' }} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient={headerGradient} />

                  <LoadingOverlay isLoading={isSubmitting} text="Sending email...">
                    <form onSubmit={handleComposeSubmit}>
                      <VStack spacing={5} align="stretch">
                        
                        {/* ENHANCEMENT: FlowSense Context Linker */}
                        <Box bg={linkBoxBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={linkBoxBorder}>
                            <HStack mb={2}>
                                <Icon as={MdLink} color="blue.500" />
                                <Text fontSize="sm" fontWeight="700" color="blue.700">Link to FlowSense Record (Optional)</Text>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <FormControl>
                                    <Select value={linkType} onChange={(e) => { setLinkType(e.target.value); setLinkId(''); }} bg={inputBg} borderRadius="lg" size="sm" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}>
                                        <option value="none">-- No Link --</option>
                                        <option value="work_order">Work Order</option>
                                        <option value="issue">Issue</option>
                                        <option value="alert">Sensor Alert</option>
                                    </Select>
                                </FormControl>
                                {linkType !== 'none' && (
                                    <FormControl>
                                        <InputGroup size="sm">
                                            <InputLeftElement><Icon as={linkType === 'work_order' ? MdBuild : MdWarning} color="gray.400"/></InputLeftElement>
                                            <Input placeholder={`Enter ${linkType.replace('_', ' ')} ID...`} value={linkId} onChange={(e) => setLinkId(e.target.value)} bg={inputBg} borderRadius="lg" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }} />
                                        </InputGroup>
                                    </FormControl>
                                )}
                            </SimpleGrid>
                        </Box>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>
                            <HStack spacing={2}><Icon as={MdPeople} color={accentColor} /><Text>Send To:</Text></HStack>
                          </FormLabel>
                          <Select value={sendMode} onChange={(e) => setSendMode(e.target.value)} bg={inputBg} borderRadius="xl" size="lg" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}>
                            <option value="single">Single Recipient</option>
                            <option value="role">User Role</option>
                          </Select>
                        </FormControl>

                        {/* Recipient Input */}
                        {sendMode === 'single' ? (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>To:</FormLabel>
                            <HStack>
                              <InputGroup size="lg">
                                <InputLeftElement><Icon as={MdEmail} color={mutedColor} /></InputLeftElement>
                                <Input type="email" list="contact-suggestions" placeholder="recipient@example.com" value={to} onChange={(e) => setTo(e.target.value)} bg={inputBg} borderRadius="xl" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }} />
                              </InputGroup>
                              <datalist id="contact-suggestions">
                                {emailContacts.map(contact => <option key={contact.id} value={contact.email} />)}
                              </datalist>
                              <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <Button size="lg" variant="outline" onClick={handleSaveContact} isLoading={isSavingContact} leftIcon={<Icon as={MdSave}/>} borderRadius="xl" colorScheme="blue">Save</Button>
                              </MotionBox>
                            </HStack>
                          </FormControl>
                        ) : (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>Select Role:</FormLabel>
                            <Select value={toRole} onChange={(e) => setToRole(e.target.value)} bg={inputBg} borderRadius="xl" size="lg" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}>
                              <option value="tech">All Technicians</option>
                              <option value="staff">All Staff</option>
                              <option value="admin">All Admins</option>
                            </Select>
                          </FormControl>
                        )}

                        {/* Template Selection */}
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>Use Template:</FormLabel>
                          <Select placeholder="Select a template (optional)" value={selectedTemplate} onChange={(e) => handleTemplateChange(e.target.value)} bg={inputBg} borderRadius="xl" size="lg" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}>
                            {emailTemplates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
                          </Select>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>Subject:</FormLabel>
                          <Input type="text" placeholder="Email subject" value={subject} onChange={(e) => setSubject(e.target.value)} bg={inputBg} borderRadius="xl" size="lg" _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }} />
                        </FormControl>

                        {/* Rich Text Editor */}
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>Body:</FormLabel>
                          <Box bg="white" color="black" borderRadius="xl" overflow="hidden" borderWidth="1px" borderColor={borderColor}>
                            <ReactQuill theme="snow" value={body} onChange={setBody} style={{ height: '250px', marginBottom: '40px' }} />
                          </Box>
                        </FormControl>

                        {/* Attachments */}
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={formLabelColor}>Attachments:</FormLabel>
                          <Input type="file" multiple onChange={handleFileSelect} display="none" id="file-upload" />
                          <Button as="label" htmlFor="file-upload" leftIcon={<Icon as={MdAttachFile} />} variant="outline" size="md" cursor="pointer" borderRadius="xl" colorScheme="blue">Add Attachments</Button>
                          
                          {attachments.length > 0 && (
                            <VStack align="stretch" mt={3} spacing={2}>
                              <AnimatePresence>
                                {attachments.map((file, index) => (
                                  <MotionBox key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                                    <HStack justify="space-between" p={3} bg={attachmentBg} borderRadius="xl">
                                      <HStack>
                                        <Icon as={MdAttachFile} color={accentColor} />
                                        <Text fontSize="sm" noOfLines={1} maxW="200px">{file.name}</Text>
                                        <Badge colorScheme="blue" borderRadius="full">{(file.size / 1024).toFixed(1)} KB</Badge>
                                      </HStack>
                                      <IconButton icon={<Icon as={MdClose} />} size="xs" variant="ghost" colorScheme="red" borderRadius="full" onClick={() => handleRemoveAttachment(index)} />
                                    </HStack>
                                  </MotionBox>
                                ))}
                              </AnimatePresence>
                            </VStack>
                          )}
                        </FormControl>

                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <LoadingProgress progress={uploadProgress} text="Uploading attachments..." />
                        )}

                        <Divider />

                        <HStack justify="space-between" wrap="wrap" gap={3}>
                          <HStack spacing={3}>
                            <MotionBox whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                              <Button type="submit" colorScheme="blue" isLoading={isSubmitting} leftIcon={<Icon as={MdSend} />} size="lg" borderRadius="xl" px={8} boxShadow="lg" _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}>
                                {sendMode === 'single' ? 'Send Email' : `Send to ${toRole}s`}
                              </Button>
                            </MotionBox>
                            
                            <Menu>
                              <MenuButton as={Button} leftIcon={<Icon as={MdDrafts} />} variant="outline" size="lg" borderRadius="xl">Draft</MenuButton>
                              <MenuList borderRadius="xl">
                                <MenuItem onClick={handleSaveDraft} borderRadius="lg"><HStack><Icon as={MdSave} /><Text>Save Current as Draft</Text></HStack></MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                        </HStack>
                      </VStack>
                    </form>
                  </LoadingOverlay>
                </Box>
              </MotionBox>
            </TabPanel>

            {/* ====================== SENT HISTORY TAB ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient="linear(to-r, purple.400, pink.500)" />
                  <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                    <HStack>
                      <Icon as={MdHistory} color={accentColor} boxSize={6} />
                      <Heading size="md" color={textColor}>Sent Email History</Heading>
                    </HStack>
                    <HStack>
                      <InputGroup maxW="250px">
                        <InputLeftElement><Icon as={MdSearch} color={mutedColor} /></InputLeftElement>
                        <Input placeholder="Search emails..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} borderRadius="xl" bg={inputBg} />
                      </InputGroup>
                      <Tooltip label="Refresh" hasArrow>
                        <IconButton icon={<Icon as={MdRefresh}/>} aria-label="Refresh History" size="md" variant="ghost" onClick={() => loadSentEmails(1)} isLoading={isLoadingHistory} borderRadius="full" colorScheme="blue" />
                      </Tooltip>
                    </HStack>
                  </Flex>
                  <Divider mb={4} />
                  
                  <DataStatus isLoading={isLoadingHistory} error={historyError} data={filteredHistory} emptyState={{ title: 'No sent emails found', description: historySearch ? 'Try a different search term' : 'Send your first email to see it here' }} loadingText="Loading email history...">
                    <TableContainer maxH="500px" overflowY="auto">
                      <Table variant='simple' size='sm'>
                        <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
                          <Tr>
                            <Th color={tableHeaderColor} borderColor={borderColor}>To</Th>
                            <Th color={tableHeaderColor} borderColor={borderColor}>Subject & Context</Th>
                            <Th color={tableHeaderColor} borderColor={borderColor}>Preview</Th>
                            <Th color={tableHeaderColor} borderColor={borderColor}>Date</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <AnimatePresence>
                            {currentHistoryData.map((email, index) => (
                              <MotionTr key={email.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }} _hover={{ bg: trHoverBg }}>
                                <Td color={textColor} borderColor={borderColor}>
                                    <HStack><Icon as={MdEmail} color={accentColor} /><Text fontWeight="600">{email.recipient_email}</Text></HStack>
                                </Td>
                                <Td color={textColor} borderColor={borderColor}>
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="500">{email.subject}</Text>
                                    <HStack wrap="wrap">
                                      {email.work_order_id && <Badge colorScheme="orange" fontSize="2xs" borderRadius="full"><Icon as={MdLink} mr={1} />WO: {email.work_order_id}</Badge>}
                                      {email.issue_id && <Badge colorScheme="red" fontSize="2xs" borderRadius="full"><Icon as={MdLink} mr={1} />Issue: {email.issue_id}</Badge>}
                                      {email.alert_id && <Badge colorScheme="yellow" fontSize="2xs" borderRadius="full"><Icon as={MdLink} mr={1} />Alert: {email.alert_id}</Badge>}
                                      
                                      {/^\[FlowSense AI\]/.test(email.subject || '') && <Badge colorScheme="purple" fontSize="2xs" borderRadius="full"><Icon as={MdAutoAwesome} mr={1} />AI</Badge>}{/* --- UPDATE --- */}
                                      {email.status === 'opened' && <Badge colorScheme="green" fontSize="2xs" borderRadius="full">Opened</Badge>}
                                      {email.status === 'bounced' && <Badge colorScheme="red" fontSize="2xs" borderRadius="full">Bounced</Badge>}
                                      {email.status === 'delivered' && <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">Delivered</Badge>}
                                    </HStack>
                                  </VStack>
                                </Td>
                                <Td color={mutedColor} borderColor={borderColor} maxW="300px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={email.body_preview}>{/* --- UPDATE: isTruncated is not a Td prop (leaked to the DOM) */}
                                    {email.body_preview || 'No preview available'}
                                </Td>
                                <Td color={mutedColor} borderColor={borderColor}>
                                    {formatDateTime(email.sent_at)}
                                </Td>
                              </MotionTr>
                            ))}
                          </AnimatePresence>
                        </Tbody>
                      </Table>
                    </TableContainer>
                    <Flex justify="space-between" align="center" mt={4} px={2} wrap="wrap" gap={3}>
                      <Text fontSize="sm" color={mutedColor}>Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.itemsPerPage, filteredHistory.length)} of {filteredHistory.length}</Text>
                      <HStack>
                        <IconButton icon={<Icon as={MdNavigateBefore}/>} aria-label="Previous Page" size="sm" onClick={pagination.prevPage} isDisabled={!pagination.hasPrevPage} borderRadius="full" variant="ghost" />
                        <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>Page {pagination.currentPage} of {pagination.totalPages || 1}</Badge>
                        <IconButton icon={<Icon as={MdNavigateNext}/>} aria-label="Next Page" size="sm" onClick={pagination.nextPage} isDisabled={!pagination.hasNextPage} borderRadius="full" variant="ghost" />
                      </HStack>
                    </Flex>
                  </DataStatus>
                </Box>
              </MotionBox>
            </TabPanel>

            {/* ====================== CONTACTS TAB ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient="linear(to-r, green.400, teal.500)" />
                  <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                    <HStack>
                      <Icon as={MdContacts} color={successColor} boxSize={6} />
                      <Heading size="md" color={textColor}>Manage Contacts</Heading>
                    </HStack>
                    <HStack>
                      <InputGroup maxW="250px">
                        <InputLeftElement><Icon as={MdSearch} color={mutedColor} /></InputLeftElement>
                        <Input placeholder="Search contacts..." value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} borderRadius="xl" bg={inputBg} />
                      </InputGroup>
                      <Tooltip label="Refresh" hasArrow>
                        <IconButton icon={<Icon as={MdRefresh}/>} aria-label="Refresh Contacts" size="md" variant="ghost" onClick={loadContacts} isLoading={isLoadingContacts} borderRadius="full" colorScheme="green" />
                      </Tooltip>
                    </HStack>
                  </Flex>
                  <Divider mb={4} />
                  
                  <DataStatus isLoading={isLoadingContacts} error={contactsError} data={filteredContacts} emptyState={{ title: 'No contacts found', description: contactSearch ? 'Try a different search term' : 'Save your first contact to see it here' }} loadingText="Loading contacts...">
                    <TableContainer maxH="500px" overflowY="auto">
                      <Table variant='simple' size='sm'>
                        <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
                          <Tr>
                            <Th color={tableHeaderColor} borderColor={borderColor}>Name</Th>
                            <Th color={tableHeaderColor} borderColor={borderColor}>Email</Th>
                            <Th color={tableHeaderColor} borderColor={borderColor} isNumeric>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <AnimatePresence>
                            {filteredContacts.map((contact, index) => (
                              <MotionTr key={contact.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }} _hover={{ bg: trHoverBg }}>
                                <Td color={textColor} borderColor={borderColor}>
                                  <HStack spacing={3}>
                                    <Avatar size="sm" name={contact.name || contact.email} bg="blue.500" color="white" />
                                    <Text fontWeight="600">{contact.name || '-'}</Text>
                                  </HStack>
                                </Td>
                                <Td color={textColor} borderColor={borderColor}>
                                    <HStack><Icon as={MdEmail} color={successColor} /><Text>{contact.email}</Text></HStack>
                                </Td>
                                <Td isNumeric borderColor={borderColor}>
                                  <Tooltip label="Delete Contact" hasArrow>
                                    <IconButton aria-label="Delete Contact" icon={<Icon as={MdDelete} />} size="sm" variant="ghost" colorScheme="red" borderRadius="full" isLoading={isDeletingContact === contact.id} onClick={() => handleDeleteContact(contact.id)} />
                                  </Tooltip>
                                </Td>
                              </MotionTr>
                            ))}
                          </AnimatePresence>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </DataStatus>
                </Box>
              </MotionBox>
            </TabPanel>

            {/* ====================== DRAFTS TAB ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient="linear(to-r, orange.400, yellow.500)" />
                  <HStack mb={4}>
                    <Icon as={MdDrafts} color="orange.500" boxSize={6} />
                    <Heading size="md" color={textColor}>Saved Drafts</Heading>
                  </HStack>
                  <Divider mb={4} />
                  
                  {savedDrafts.length === 0 ? (
                    <EmptyState title="No drafts saved" description="Save your current email as a draft to see it here" icon={<Icon as={MdDrafts} boxSize={12} color={mutedColor} />} />
                  ) : (
                    <VStack align="stretch" spacing={3}>
                      <AnimatePresence>
                        {savedDrafts.map((draft, index) => (
                          <MotionBox key={draft.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ delay: index * 0.05 }} p={4} borderWidth="2px" borderRadius="2xl" borderColor={borderColor} _hover={{ borderColor: 'orange.400', boxShadow: 'md' }}>
                            <Flex justify="space-between" align="start" wrap="wrap" gap={3}>
                              <Box flex={1}>
                                <HStack mb={2}><Icon as={MdDrafts} color="orange.500" /><Text fontWeight="bold" color={textColor}>{draft.name}</Text></HStack>
                                <VStack align="start" spacing={1}>
                                  <HStack><Text fontSize="sm" color={mutedColor}>To:</Text><Text fontSize="sm" color={textColor}>{draft.to || draft.toRole}</Text></HStack>
                                  <HStack><Text fontSize="sm" color={mutedColor}>Subject:</Text><Text fontSize="sm" color={textColor} noOfLines={1} maxW="300px">{draft.subject}</Text></HStack>
                                  {draft.linkId && <HStack><Text fontSize="sm" color={mutedColor}>Linked:</Text><Badge colorScheme="blue" fontSize="2xs" borderRadius="full">{draft.linkType.toUpperCase()} - {draft.linkId}</Badge></HStack>}
                                </VStack>
                                <Text fontSize="xs" color={mutedColor} mt={2}>Saved: {format(new Date(draft.savedAt), 'PPp')}</Text>
                              </Box>
                              <HStack>
                                <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button size="sm" onClick={() => handleLoadDraft(draft)} colorScheme="blue" borderRadius="xl">Load</Button>
                                </MotionBox>
                                <Tooltip label="Delete Draft" hasArrow>
                                    <IconButton icon={<Icon as={MdDelete} />} size="sm" variant="ghost" colorScheme="red" borderRadius="full" onClick={() => handleDeleteDraft(draft.id)} />
                                </Tooltip>
                              </HStack>
                            </Flex>
                          </MotionBox>
                        ))}
                      </AnimatePresence>
                    </VStack>
                  )}
                </Box>
              </MotionBox>
            </TabPanel>

            {/* ====================== TEMPLATES TAB ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient="linear(to-r, purple.400, pink.500)" />
                  <TemplateManagement />
                </Box>
              </MotionBox>
            </TabPanel>

            {/* ====================== AI ESCALATIONS TAB (--- UPDATE) ====================== */}
            <TabPanel p={0}>
              <MotionBox initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                  <Box position="absolute" top={0} left={0} right={0} h="4px" bgGradient="linear(to-r, purple.500, teal.400)" />
                  <SlaEscalationMailPanel onDraft={handleAiDraft} />
                </Box>
              </MotionBox>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* DELETE CONFIRMATION DIALOG */}
        <ConfirmDialog isOpen={isDeleteOpen} onClose={onDeleteClose} onConfirm={confirmDeleteContact} title="Delete Contact" message="Are you sure you want to delete this contact? This action cannot be undone." confirmText="Delete" colorScheme="red" isLoading={isDeletingContact !== null} />

        {/* DEBUG DRAWER */}
        <Drawer isOpen={isDebugOpen} placement="right" onClose={onDebugClose} size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdBugReport} color="yellow.500" /><Text>Debug Panel</Text></HStack></DrawerHeader>
            <DrawerBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Heading size="sm" mb={3}>Current State</Heading>
                  <SimpleGrid columns={2} spacing={2} fontSize="sm">
                    <Text fontWeight="600">Send Mode:</Text><Text>{sendMode}</Text>
                    <Text fontWeight="600">Emails Sent:</Text><Text>{sentEmails.length}</Text>
                    <Text fontWeight="600">Contacts:</Text><Text>{emailContacts.length}</Text>
                    <Text fontWeight="600">Drafts:</Text><Text>{savedDrafts.length}</Text>
                    <Text fontWeight="600">Templates:</Text><Text>{emailTemplates.length}</Text>
                    <Text fontWeight="600">Attachments:</Text><Text>{attachments.length}</Text>
                  </SimpleGrid>
                </Box>
                <Divider />
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading size="sm">Debug Logs</Heading>
                    <HStack>
                      <Select size="sm" value={debugFilter} onChange={(e) => setDebugFilter(e.target.value)} maxW="100px" borderRadius="md"><option value="all">All</option><option value="debug">Debug</option><option value="info">Info</option><option value="warn">Warn</option><option value="error">Error</option><option value="success">Success</option></Select>
                      <Button size="sm" onClick={() => logger.clear()} colorScheme="red" variant="ghost">Clear</Button>
                    </HStack>
                  </Flex>
                  <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto" bg={debugVStackBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                    {filteredDebugLogs.slice(-50).reverse().map((log, index) => (
                      <Box key={index} p={2} bg={debugRowBg} borderRadius="md" fontSize="xs" fontFamily="mono">
                        <Flex justify="space-between" mb={1}>
                          <Badge colorScheme={log.level === 'error' ? 'red' : log.level === 'warn' ? 'orange' : log.level === 'success' ? 'green' : log.level === 'info' ? 'blue' : 'gray'} fontSize="10px">{log.level.toUpperCase()}</Badge>
                          <Text color="gray.500" fontSize="10px">{new Date(log.timestamp).toLocaleTimeString()}</Text>
                        </Flex>
                        <Text fontWeight="500">{log.message}</Text>
                        {log.data && <Text color="gray.500" fontSize="10px" mt={1}>{JSON.stringify(log.data).substring(0, 100)}</Text>}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </DrawerBody>
            <DrawerFooter borderTopWidth="1px">
              <Button size="sm" mr={3} onClick={() => { const logs = logger.export(); const blob = new Blob([logs], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `mail-debug-logs-${Date.now()}.json`; a.click(); }} leftIcon={<Icon as={MdDownload} />}>Export Logs</Button>
              <Button size="sm" onClick={onDebugClose} colorScheme="blue">Close</Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </ErrorBoundary>
  );
}

export default MailPageEnhanced;